import isEqual from 'lodash/isEqual';
import { useEffect, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppHeader } from '@components/AppHeader/AppHeader';
import DateAndTime from '@components/DateAndTime/DateAndTime';
import Loader from '@components/Loader/Loader';
import MapMarkers from '@components/Map/MapMarkers/MapMarkers';
import MapRoute from '@components/Map/MapRoute/MapRoute';
import MapView, { type Region } from '@components/Map/MapView/MapView';
import { TripDetailInfo } from '@components/TripDetailInfo/TripDetailInfo';
import TripStatus from '@components/TripStatus/TripStatus';
import { ERoutes } from '@enums/routes.enum';
import useTripSocket from '@hooks/useTripSocket';
import type { RootState } from '@store/store';
import { resetCurrentTrip, type Trip } from '@store/trips/tripsSlice';

import classes from './TripInfo.module.scss';
import { handleMakeRegion } from './TripInfo.utils';

const TripInfo = () => {
	const [isCollapse, setIsCollapse] = useState<boolean>(false);
	const [region, setRegion] = useState<Region | undefined>(undefined);

	const trips = useSelector((state: RootState) => state.trips.data);
	const id = useSelector((state: RootState) => state.trips.currentTrip?.id);
	const status = useSelector((state: RootState) => state.trips.currentTrip?.status);
	const date = useSelector((state: RootState) => state.trips.currentTrip?.date);
	const route = useSelector((state: RootState) => state.trips.currentTrip?.route);
	const stops = useSelector((state: RootState) => state.trips.currentTrip?.stops || [], isEqual);

	const showGoBack = trips?.length > 1;
	const navigate = useNavigate();
	const { handleTripsSocketEvents } = useTripSocket();
	const dispatch = useDispatch();
	const mapRef = useRef<MapRef>(null);

	const handleGoBack = () => {
		dispatch(resetCurrentTrip());
		navigate(ERoutes.trips);
	};

	const handleCollapse = () => {
		setIsCollapse((prev) => !prev);
	};

	useEffect(() => {
		if (trips?.length) {
			const socketTripsIds = trips.map((trip: Trip) => trip.id);
			handleTripsSocketEvents(socketTripsIds);
		}
	}, [trips?.length]);

	useEffect(() => {
		if (route) {
			const newRegion = handleMakeRegion(route.coordinates, isCollapse);
			setRegion(newRegion);
		}
	}, [route, isCollapse]);

	return id ? (
		<div className={classes['container']}>
			<AppHeader goBack={showGoBack ? handleGoBack : undefined} />
			{date && status && (
				<div className={classes['headerContainer']}>
					<TripStatus status={status} />
					<DateAndTime date={date} size={16} />
				</div>
			)}
			{region && (
				<MapView fullScreen={isCollapse} mapRef={mapRef} region={region}>
					{route && <MapRoute route={route} />}
					{route && stops.length > 0 && <MapMarkers markers={stops} />}
				</MapView>
			)}
			<TripDetailInfo isCollapse={isCollapse} onCollapse={handleCollapse} />
		</div>
	) : (
		<Loader />
	);
};

export default TripInfo;
